import {Dialog} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {InstalledBaseNode, InstalledBaseNodeType} from "../../../codegen/graphql";
import {useTheme} from "@mui/joy";
import useMediaQuery from "@mui/system/useMediaQuery";
import CreateNodeForm from "./createNodeForm";
import NodeTypeSelect from "./nodeTypeSelect";
import RegisterAssetModal from "../../RegisterAssetModal";
import DuplicateNodeWizard from "../../DuplicateNodeWizard";

interface Props {
    node: InstalledBaseNode;
    refetchFunction?: () => void;
    open: boolean,
    createNodeCallback?: (nodeId: string) => void
    onClose?: () => void,
    /**
     * Pre-selected node type: the type selection is skipped and the modal opens directly
     * on the matching form. Used by the empty-column guidance, whose buttons already name
     * the type ("Create System", "Add Asset").
     */
    initialNodeType?: InstalledBaseNodeType,
    /**
     * Always show the type selection, even when a single recommendation would normally be
     * auto-selected. Required for the "Create other node" action — without it the user
     * would be bounced straight into the form for that one recommendation.
     */
    forceTypeSelection?: boolean,
}

/** Which step a given node type belongs to (see getSteps). */
const stepForNodeType = (nodeType?: InstalledBaseNodeType) => {
    if (!nodeType) return 0;
    return nodeType.key === "ASSET" ? 2 : 1;
};

const CreateNodeModal = ({
                             node,
                             refetchFunction,
                             open,
                             createNodeCallback,
                             onClose,
                             initialNodeType,
                             forceTypeSelection,
                         }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    // Seeded from the pre-selected type so a guided open never flashes the selection
    // screen before the effect below moves it along.
    const [activeStep, setActiveStep] = useState(() => stepForNodeType(initialNodeType));

    const [nodeType, setNodeType] = useState<InstalledBaseNodeType | undefined>(initialNodeType);
    // Tracks whether the user explicitly returned to the selection screen (via back/edit).
    // While true, the single-recommended auto-skip is suppressed so they aren't bounced
    // straight back out of the selection they asked to see.
    const [returnedToSelection, setReturnedToSelection] = useState(!!forceTypeSelection);
    // Set via "Save & Duplicate": the just-created node becomes the template and the
    // modal advances to the duplication wizard step.
    const [duplicateTemplate, setDuplicateTemplate] = useState<{ nodeId: string; title: string } | null>(null);

    // Re-arm the auto-skip on each fresh open — unless the caller explicitly asked for the
    // selection screen, in which case the suppression has to survive.
    useEffect(() => {
        if (open) setReturnedToSelection(!!forceTypeSelection);
    }, [open, forceTypeSelection]);

    // onExited (modal fully closed): reset for the next open.
    const handleExited = () => {
        setNodeType(initialNodeType);
        setReturnedToSelection(!!forceTypeSelection);
        setDuplicateTemplate(null);
    }

    // Back/edit from a later step: show the selection and keep it shown.
    const handleBack = () => {
        setNodeType(undefined);
        setReturnedToSelection(true);
    }

    const getSteps = () => [
        {
            id: 0,
            step: <NodeTypeSelect
                node={node}
                onClose={onClose}
                onNodeTypeSelect={(type) => setNodeType(type)}
                allowAutoSelect={!returnedToSelection}
            />,
            condition: () => true,
        },
        {
            id: 1,
            step: <CreateNodeForm
                node={node}
                refetchFunction={refetchFunction}
                onClose={onClose}
                createNodeType={nodeType ?? undefined}
                createNodeCallback={createNodeCallback}
                onBack={handleBack}
                onDuplicateRequest={(template) => setDuplicateTemplate(template)}
            />,
            condition: () => !!nodeType && nodeType.key !== "ASSET",
        },
        {
            id: 2,
            step: <RegisterAssetModal
                isModal={false}
                onClose={onClose}
                onBack={handleBack}
                nodeType={nodeType}
                parentInstalledBaseNodeId={node.id}
                onComplete={refetchFunction}
                createNodeCallback={createNodeCallback}
            />,
            condition: () => nodeType?.key === "ASSET",
        },
        {
            id: 3,
            step: duplicateTemplate && nodeType ? <DuplicateNodeWizard
                template={{
                    templateNodeId: duplicateTemplate.nodeId,
                    templateTitle: duplicateTemplate.title,
                    nodeTypeKey: nodeType.key,
                    parentNodeId: node.id,
                }}
                onClose={onClose}
                refetchFunction={refetchFunction}
            /> : null,
            condition: () => !!duplicateTemplate && !!nodeType,
        },
    ]

    useEffect(() => {
        const steps = getSteps();
        for (let i = steps.length - 1; i >= 0; i--) {
            if (steps[i].condition()) {
                setActiveStep(steps[i].id);
                break;
            }
        }
    }, [nodeType, duplicateTemplate]);

    const steps = getSteps();
    const currentStep = steps.find((step) => step.id === activeStep)

    return <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isFullScreen}
        slotProps={{
            transition: {onExited: handleExited},
            paper: {
                sx: {
                    overflowX: "hidden"
                },
            },
        }}
    >
        {currentStep?.step}
    </Dialog>
};

export default CreateNodeModal;
